import { trackPublicCta } from '~publish/helpers/publicTracking'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import type { AnimationEvent, JSX, PropsWithChildren } from 'react'

import { format } from 'date-fns'
import { TZDate } from '@date-fns/tz'

import clsx from 'clsx'

import { Button, Flex, Link, Paragraph, Text } from '@buffer-mono/popcorn'

import {
  PublicPreviewBar,
  usePreviewModeSearchParam,
} from '~publish/components/PublicPreviewBar'

import { FollowersView } from '../AllChannelsInsights/sections/ChartViewToggle'
import type { PostsView } from '../AllChannelsInsights/sections/ChartViewToggle'
import { DateRangeToggle } from '../components/DateRangeToggle'
import { InsightsToolbar } from '../components/InsightsToolbar'
import { getTopPostsPageCursor } from '../InsightsChannel/topPostsCursor'
import { calculateDateRange } from '../utils'
import {
  PUBLIC_INSIGHTS_TOP_POSTS_PER_PAGE,
  PublicInsightsChannelMetrics,
  PublicInsightsLoadingState,
  PublicInsightsSummary,
  PublicInsightsTopPosts,
} from './PublicInsightsSections'
import type { PublicInsightsPerformanceSort } from './PublicInsightsSections'
import { usePublicInsightsSample } from './publicInsightsSampleQuery'
import type { PublicInsightsSample } from './publicInsightsSampleQuery'
import { PublicInsightsPlaceholderSections } from './PublicInsightsPlaceholders'
import { PublicInsightsChannelFilter } from './PublicInsightsChannelFilter'
import { PublicInsightsCharts } from './PublicInsightsCharts'

import styles from './InsightsSamplePreview.module.css'

type SampleView = 'preview' | 'sample'

type SamplePreviewState = {
  view: SampleView
  setView: (view: SampleView) => void
}

const SamplePreviewContext = createContext<SamplePreviewState | null>(null)

function useSamplePreview(): SamplePreviewState {
  const context = useContext(SamplePreviewContext)
  if (!context) throw new Error('InsightsSamplePreview is required')
  return context
}

function InsightsSamplePreviewRoot({
  children,
}: PropsWithChildren): JSX.Element {
  const [on, setOn] = usePreviewModeSearchParam()
  const view: SampleView = on ? 'sample' : 'preview'
  const setView = useCallback(
    (nextView: SampleView): void => setOn(nextView === 'sample'),
    [setOn],
  )
  const context = useMemo(() => ({ view, setView }), [view, setView])
  return (
    <SamplePreviewContext.Provider value={context}>
      {children}
    </SamplePreviewContext.Provider>
  )
}

function InsightsSamplePreviewToggle(): JSX.Element {
  const { view, setView } = useSamplePreview()
  const on = view === 'sample'
  return (
    <PublicPreviewBar
      on={on}
      onChange={(checked): void => {
        if (checked)
          trackPublicCta('publish-insights-emptyState-viewSampleData-1')
        setView(checked ? 'sample' : 'preview')
      }}
      offLabel="Explore Insights with Buffer’s real data"
      onLabel="You’re viewing Buffer’s real data"
      aboutLabel="About these numbers"
      aboutTooltip="These are Buffer’s own numbers"
      about={
        on && (
          <Flex direction="column" gap="xs">
            <Paragraph size="md">
              This is live data from Buffer’s own social accounts. We share it
              openly so you can explore Insights before connecting a channel.
            </Paragraph>
            <Link href="https://buffer.com/open" external>
              See more of Buffer’s numbers
            </Link>
          </Flex>
        )
      }
    />
  )
}

function InsightsSampleSections({
  enabled,
}: {
  enabled: boolean
}): JSX.Element {
  return (
    <Flex
      direction="column"
      gap="lg"
      align="stretch"
      fullWidth
      className={styles.sample}
    >
      <PublicInsightsSampleContent key={String(enabled)} enabled={enabled} />
    </Flex>
  )
}

function InsightsSamplePreviewContent(): JSX.Element {
  const { view } = useSamplePreview()
  return <InsightsSampleSections enabled={view === 'sample'} />
}

export const InsightsSamplePreview = Object.assign(InsightsSamplePreviewRoot, {
  Toggle: InsightsSamplePreviewToggle,
  Content: InsightsSamplePreviewContent,
})

function PublicInsightsSampleContent({
  enabled,
}: {
  enabled: boolean
}): JSX.Element {
  const [selectedDateRange, setSelectedDateRange] = useState('last-30-days')
  const [range, setRange] = useState(() =>
    calculateDateRange('last-30-days', TZDate.tz('UTC')),
  )
  const [channelIds, setChannelIds] = useState<string[]>([])
  const [sort, setSort] = useState<'reactions' | 'comments'>('reactions')
  const [topPostsPage, setTopPostsPage] = useState(1)
  const [paginationSample, setPaginationSample] =
    useState<PublicInsightsSample | null>(null)
  const [followersView, setFollowersView] = useState<FollowersView>(
    FollowersView.BY_CHANNEL,
  )
  const [postsView, setPostsView] = useState<PostsView>('bar')
  const [performanceSort, setPerformanceSort] =
    useState<PublicInsightsPerformanceSort>({
      key: 'postCount',
      direction: 'desc',
    })
  const [revealPlayed, setRevealPlayed] = useState(false)
  const { data, dataState, previousData, loading, error, refetch } =
    usePublicInsightsSample(
      {
        input: {
          startDate: `${format(range.startDate, 'yyyy-MM-dd')}T00:00:00.000Z`,
          endDate: `${format(range.endDate, 'yyyy-MM-dd')}T00:00:00.000Z`,
          ...(channelIds.length > 0 ? { channelIds } : {}),
        },
        sort: { field: sort, direction: 'desc' },
        after: getTopPostsPageCursor(
          topPostsPage,
          PUBLIC_INSIGHTS_TOP_POSTS_PER_PAGE,
        ),
      },
      { skip: !enabled },
    )
  const result =
    dataState === 'complete' ? data.publicInsightsSample : undefined
  const sample =
    !loading && !error && result?.__typename === 'PublicInsightsSampleSuccess'
      ? result
      : paginationSample
  const errorMessage = error
    ? 'We couldn’t load Buffer’s insights. Please try again.'
    : result?.__typename === 'PublicInsightsSampleInvalidInput'
      ? 'Choose a valid date range of up to 93 days, ending today or earlier.'
      : result?.__typename === 'LimitReachedError'
        ? 'Too many requests. Wait a moment, then try again.'
        : 'Buffer’s sample insights are temporarily unavailable.'
  const retry = (): void => {
    void refetch().catch(() => undefined)
  }
  const channelSource =
    sample?.__typename === 'PublicInsightsSampleSuccess'
      ? sample
      : previousData?.publicInsightsSample
  const channels =
    channelSource?.__typename === 'PublicInsightsSampleSuccess'
      ? channelSource.availableChannels
      : []
  const changeRange = (value: string, nextRange: typeof range): void => {
    setPaginationSample(null)
    setSelectedDateRange(value)
    setRange(nextRange)
    setTopPostsPage(1)
  }
  const endReveal = (event: AnimationEvent<HTMLDivElement>): void => {
    if (event.target === event.currentTarget.lastElementChild) {
      setRevealPlayed(true)
    }
  }
  return (
    <Flex direction="column" gap="lg" align="stretch" fullWidth>
      <fieldset
        disabled={!enabled}
        className={styles.toolbar}
        aria-label="Insights filters"
      >
        <InsightsToolbar>
          <DateRangeToggle
            disabled={!enabled}
            weekStartsOn={1}
            selectedDateRange={selectedDateRange}
            currentRange={range}
            onDateRangeChange={(value): void => {
              if (value === 'custom') return
              changeRange(value, calculateDateRange(value, TZDate.tz('UTC')))
            }}
            onCustomRangeApply={(startDate, endDate): void =>
              changeRange('custom', { startDate, endDate })
            }
          />
          <InsightsToolbar.Filters>
            <PublicInsightsChannelFilter
              channels={channels}
              value={channelIds}
              onChange={(ids): void => {
                setPaginationSample(null)
                setChannelIds(ids)
                setTopPostsPage(1)
              }}
            />
          </InsightsToolbar.Filters>
        </InsightsToolbar>
      </fieldset>
      <div
        className={clsx(
          styles.sections,
          enabled && sample && !revealPlayed && styles.sectionsReveal,
        )}
        onAnimationEnd={endReveal}
      >
        {!enabled ? (
          <PublicInsightsPlaceholderSections />
        ) : loading && !paginationSample ? (
          <PublicInsightsLoadingState />
        ) : sample ? (
          <>
            <PublicInsightsSummary sample={sample} />
            <PublicInsightsTopPosts
              sample={sample}
              sort={sort}
              page={topPostsPage}
              loading={loading}
              error={
                !loading &&
                (error || result?.__typename !== 'PublicInsightsSampleSuccess')
                  ? errorMessage
                  : undefined
              }
              onRetry={
                result?.__typename !== 'PublicInsightsSampleInvalidInput'
                  ? retry
                  : undefined
              }
              onSortChange={(value): void => {
                setPaginationSample(null)
                setSort(value)
                setTopPostsPage(1)
              }}
              onPageChange={(page): void => {
                setPaginationSample(sample)
                setTopPostsPage(page)
              }}
            />
            <PublicInsightsChannelMetrics
              sample={sample}
              sortConfig={performanceSort}
              onSort={(key): void =>
                setPerformanceSort({
                  key,
                  direction:
                    performanceSort.key === key &&
                    performanceSort.direction === 'desc'
                      ? 'asc'
                      : 'desc',
                })
              }
            />
            <PublicInsightsCharts
              sample={sample}
              hasChannelFilter={channelIds.length > 0}
              followersView={followersView}
              onFollowersViewChange={setFollowersView}
              postsView={postsView}
              onPostsViewChange={setPostsView}
            />
          </>
        ) : (
          <Flex
            as={error ? 'div' : 'output'}
            role={error ? 'alert' : undefined}
            direction="column"
            gap="sm"
          >
            <Text>{errorMessage}</Text>
            {result?.__typename !== 'PublicInsightsSampleInvalidInput' && (
              <Button variant="secondary" onClick={retry}>
                Try again
              </Button>
            )}
          </Flex>
        )}
      </div>
    </Flex>
  )
}
